/** One sales document: the printed content, its history and its actions. */
import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, Ban, FileText, HandCoins, Pencil, Printer, Send, Trash2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { amount, dec } from '../../lib/money';
import { dateTime, shortDate } from '../../lib/format';
import { openFile } from '../../lib/files';
import { ReasonDialog } from '../../components/ReasonDialog';
import { Alert, Button, Card, Field, Modal, Money, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, inputClass } from '../../components/ui';
import { KIND_LABEL, STATUS_LABEL, STATUS_TONE, type SalesDetail, type SalesKind } from './types';

const NEXT: Partial<Record<SalesKind, SalesKind[]>> = {
  QUOTATION: ['SALES_ORDER', 'SALES_INVOICE'],
  SALES_ORDER: ['DELIVERY_RECEIPT', 'SALES_INVOICE'],
  DELIVERY_RECEIPT: ['SALES_INVOICE'],
};

export function SalesDetailPage(): JSX.Element {
  const { id } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as { warnings?: string[]; issueError?: string };
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'cancel' | 'discard' | 'serial' | null>(null);
  const [manualSerial, setManualSerial] = useState('');
  const [error, setError] = useState<string | null>(state.issueError ?? null);
  const [warnings, setWarnings] = useState<string[]>(state.warnings ?? []);

  const doc = useQuery({ queryKey: ['sales-doc', id], queryFn: () => api<SalesDetail>(`/sales-documents/${id}`) });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['sales'] });
    void queryClient.invalidateQueries({ queryKey: ['sales-doc', id] });
  };
  const fail = (caught: unknown): void => {
    if (caught instanceof ApiError && caught.code === 'SERIAL_REQUIRED') {
      setDialog('serial');
      setError(null);
      return;
    }
    setError(caught instanceof ApiError ? caught.message : 'That did not work.');
  };

  const issue = useMutation({
    mutationFn: (body: { manualSerial?: number; overrideCreditLimit?: boolean }) => api<SalesDetail>(`/sales-documents/${id}/issue`, { method: 'POST', body }),
    onSuccess: (d) => {
      setDialog(null);
      setError(null);
      setWarnings(d.warnings ?? []);
      refresh();
    },
    onError: fail,
  });
  const cancel = useMutation({
    mutationFn: (reason: string) => api(`/sales-documents/${id}/cancel`, { method: 'POST', body: { reason } }),
    onSuccess: () => { setDialog(null); refresh(); },
    onError: fail,
  });
  const discard = useMutation({
    mutationFn: () => api(`/sales-documents/${id}`, { method: 'DELETE' }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['sales'] }); navigate(-1); },
    onError: fail,
  });
  const convert = useMutation({
    mutationFn: (target: SalesKind) => api<SalesDetail>(`/sales-documents/${id}/convert`, { method: 'POST', body: { target } }),
    onSuccess: (d) => { refresh(); navigate(`/sales/${d.id}/edit`); },
    onError: fail,
  });
  const print = useMutation({ mutationFn: () => openFile(`/sales-documents/${id}/pdf`), onSuccess: refresh, onError: fail });

  if (doc.isLoading) return <Skeleton className="h-96 w-full" />;
  if (!doc.data) return <Alert tone="critical">Could not load this document.</Alert>;
  const d = doc.data;
  const receivable = d.kind === 'SALES_INVOICE' || d.kind === 'DEBIT_MEMO';
  const issueLabel = d.kind === 'QUOTATION' || d.kind === 'SALES_ORDER' ? 'Send' : 'Issue';

  return (
    <>
      <PageHeader
        title={`${KIND_LABEL[d.kind]} ${d.number ?? '(draft)'}`}
        description={`${d.customer.registeredName} · ${shortDate(d.date)} · ${d.branch.code} ${d.branch.name}`}
        actions={
          <>
            <StatusChip tone={STATUS_TONE[d.status]}>{STATUS_LABEL[d.status]}</StatusChip>
            {d.status === 'DRAFT' && can('sales.create', 'sales.manage') && (
              <>
                <Link to={`/sales/${d.id}/edit`}><Button variant="secondary"><Pencil aria-hidden="true" className="h-4 w-4" /> Edit</Button></Link>
                <Button variant="secondary" onClick={() => setDialog('discard')}><Trash2 aria-hidden="true" className="h-4 w-4" /> Discard</Button>
              </>
            )}
            {d.status === 'DRAFT' && can('sales.manage') && (
              <Button busy={issue.isPending} onClick={() => issue.mutate({})}><Send aria-hidden="true" className="h-4 w-4" /> {issueLabel}</Button>
            )}
            <Button variant="secondary" busy={print.isPending} onClick={() => print.mutate()}>
              <Printer aria-hidden="true" className="h-4 w-4" /> {d.status === 'DRAFT' ? 'Preview' : d.printCount > 0 ? 'Reprint' : 'Print'}
            </Button>
            {d.status === 'ISSUED' && receivable && dec(d.balanceDue).gt(0) && can('receipts.create', 'receipts.manage') && (
              <Link to={`/receipts/new?contact=${d.contactId}&invoice=${d.id}`}><Button><HandCoins aria-hidden="true" className="h-4 w-4" /> Record payment</Button></Link>
            )}
            {d.status === 'ISSUED' && d.kind === 'SALES_INVOICE' && can('sales.manage') && (
              <Link to={`/sales/new/credit_memo?contact=${d.contactId}&invoice=${d.id}`}><Button variant="secondary"><FileText aria-hidden="true" className="h-4 w-4" /> Credit memo</Button></Link>
            )}
            {d.status === 'ISSUED' && (NEXT[d.kind] ?? []).map((k) => (
              <Button key={k} variant="secondary" busy={convert.isPending} onClick={() => convert.mutate(k)}><ArrowRightLeft aria-hidden="true" className="h-4 w-4" /> To {KIND_LABEL[k].toLowerCase()}</Button>
            ))}
            {d.status === 'ISSUED' && ['SALES_INVOICE', 'CREDIT_MEMO', 'DEBIT_MEMO', 'DELIVERY_RECEIPT'].includes(d.kind) && can('sales.manage', 'void.approve') && (
              <Button variant="danger" onClick={() => setDialog('cancel')}><Ban aria-hidden="true" className="h-4 w-4" /> Cancel</Button>
            )}
          </>
        }
      />
      {error && !dialog && <div className="mb-4"><Alert tone="critical">{error}</Alert></div>}
      {warnings.map((w) => <div key={w} className="mb-4"><Alert tone="warning">{w}</Alert></div>)}
      {d.status === 'CANCELLED' && <div className="mb-4"><Alert tone="critical">Cancelled {dateTime(d.cancelledAt)}: {d.cancelReason}. The serial stays on record and appears in the Sales Journal at zero value.</Alert></div>}

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <Card>
          <div className="mb-4 grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <p className="text-slate-500">{d.kind === 'DELIVERY_RECEIPT' ? 'Delivered to' : 'Sold to'}</p>
              <p className="font-semibold">{d.buyerName || d.customer.registeredName}</p>
              <p>{d.buyerAddress || d.customer.address || '—'}</p>
              <p className="tabular-nums">TIN {d.buyerTin || d.customer.tinFormatted || '—'}</p>
            </div>
            <div className="sm:text-right">
              {d.dueDate && <p><span className="text-slate-500">Due</span> {shortDate(d.dueDate)}</p>}
              {d.reference && <p><span className="text-slate-500">Reference</span> {d.reference}</p>}
              {d.invoice && <p><span className="text-slate-500">Adjusts</span> <Link className="text-brand-700 underline dark:text-brand-100" to={`/sales/${d.invoice.id}`}>{d.invoice.serial}</Link></p>}
              {d.source && <p><span className="text-slate-500">From</span> <Link className="text-brand-700 underline dark:text-brand-100" to={`/sales/${d.source.id}`}>{d.source.serial ?? d.source.internalNumber}</Link></p>}
              {d.scPwdType && <p><span className="text-slate-500">{d.scPwdType === 'PWD' ? 'PWD' : 'Senior citizen'}</span> {d.scPwdName} · {d.scPwdIdNumber}</p>}
            </div>
          </div>
          <TableWrap>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <Th>Description</Th>
                <Th align="right">Qty</Th>
                <Th align="right">Unit price</Th>
                <Th>Tax</Th>
                <Th align="right">Amount</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {d.lines.map((l) => (
                <tr key={l.id}>
                  <Td>{l.description}</Td>
                  <Td align="right" className="tabular-nums">{Number(l.quantity)} {l.uom}</Td>
                  <Td align="right"><Money>{amount(l.unitPrice)}</Money></Td>
                  <Td className="whitespace-nowrap">{l.taxCode}</Td>
                  <Td align="right"><Money>{amount(l.grossAmount)}</Money></Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <dl className="ml-auto mt-4 max-w-xs space-y-1 text-sm">
            {[
              ['VATable sales', d.vatableSales],
              ['Sales to government', d.governmentSales],
              ['VAT-exempt sales', d.exemptSales],
              ['Zero-rated sales', d.zeroRatedSales],
              ['VAT (12%)', d.vatAmount],
            ]
              .filter(([, v]) => !dec(v).isZero())
              .map(([label, v]) => (
                <div key={label} className="flex justify-between gap-6"><dt className="text-slate-500">{label}</dt><dd><Money>{amount(v)}</Money></dd></div>
              ))}
            {!dec(d.scPwdDiscount).isZero() && <div className="flex justify-between gap-6"><dt className="text-slate-500">SC/PWD discount</dt><dd><Money>-{amount(d.scPwdDiscount)}</Money></dd></div>}
            <div className="flex justify-between gap-6 border-t border-slate-200 pt-1 text-base font-bold dark:border-slate-700"><dt>Total</dt><dd><Money>{amount(d.totalAmount)}</Money></dd></div>
            {receivable && d.status === 'ISSUED' && <div className="flex justify-between gap-6 font-semibold text-brand-700 dark:text-brand-100"><dt>Balance due</dt><dd><Money>{amount(d.balanceDue)}</Money></dd></div>}
          </dl>
          {d.notes && <p className="mt-4 whitespace-pre-line text-sm text-slate-600 dark:text-slate-300">{d.notes}</p>}
        </Card>

        <div className="space-y-4">
          {(d.receipts.length > 0 || d.memos.length > 0) && (
            <Card title="Payments & adjustments">
              <ul className="space-y-2 text-sm">
                {d.receipts.map((r) => (
                  <li key={r.receiptId} className="flex justify-between gap-2">
                    <Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/receipts/${r.receiptId}`}>{r.number}</Link>
                    <span className={r.status === 'VOIDED' ? 'line-through opacity-60' : ''}><Money>{amount(r.amount)}</Money></span>
                  </li>
                ))}
                {d.memos.map((m) => (
                  <li key={m.id} className="flex justify-between gap-2">
                    <Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/sales/${m.id}`}>{KIND_LABEL[m.kind]} {m.serial ?? '(draft)'}</Link>
                    <span className={m.status === 'CANCELLED' ? 'line-through opacity-60' : ''}><Money>{amount(m.totalAmount)}</Money></span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <Card title="Record">
            <dl className="space-y-2 text-sm">
              {d.issuedAt && <div><dt className="text-slate-500">Issued</dt><dd>{dateTime(d.issuedAt)}</dd></div>}
              <div><dt className="text-slate-500">Prints</dt><dd>{d.printCount === 0 ? 'Not printed yet' : `${d.printCount} (${d.printCount - 1} reprint${d.printCount === 2 ? '' : 's'})`}</dd></div>
              {d.journalEntryId && <div><dt className="text-slate-500">Ledger</dt><dd><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/journals/${d.journalEntryId}`}>View journal entry</Link></dd></div>}
              {d.derived.map((x) => <div key={x.id}><dt className="text-slate-500">Carried into</dt><dd><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/sales/${x.id}`}>{KIND_LABEL[x.kind]} {x.serial ?? x.internalNumber ?? '(draft)'}</Link></dd></div>)}
            </dl>
          </Card>
        </div>
      </div>

      {dialog === 'cancel' && (
        <ReasonDialog
          title={`Cancel ${d.number}?`}
          description="The document keeps its serial and is marked CANCELLED; its ledger entry is reversed. This cannot be undone."
          confirmLabel="Cancel document"
          error={error}
          busy={cancel.isPending}
          onClose={() => { setDialog(null); setError(null); }}
          onConfirm={(reason) => cancel.mutate(reason)}
        />
      )}
      {dialog === 'discard' && (
        <Modal title="Discard this draft?" onClose={() => setDialog(null)}>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDialog(null)}>Keep it</Button>
            <Button variant="danger" busy={discard.isPending} onClick={() => discard.mutate()}>Discard</Button>
          </div>
        </Modal>
      )}
      {dialog === 'serial' && (
        <Modal title="Serial from the booklet" description="This branch uses pre-printed (ATP) invoices. Enter the number printed on the form you are using." onClose={() => setDialog(null)}>
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); issue.mutate({ manualSerial: Number(manualSerial) }); }}>
            {error && <Alert tone="critical">{error}</Alert>}
            <Field label="Serial number" required>
              <input className={inputClass} inputMode="numeric" value={manualSerial} onChange={(e) => setManualSerial(e.target.value.replace(/\D/g, ''))} autoFocus />
            </Field>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setDialog(null)}>Cancel</Button>
              <Button type="submit" busy={issue.isPending} disabled={!manualSerial}>Issue</Button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
